import React from "react";
import { useTranslation } from "react-i18next";

/* -------------------------------------------------------------
   Brand logo data – kept as a plain constant array.
   Added explicit type for better IntelliSense & future‑proofing.
------------------------------------------------------------- */
type Brand = {
  src: string;
  alt: string;
  title: string;
};

const brandLogos: Brand[] = [
  {
    src: "https://static.readdy.ai/image/87705512d5c7f2a4b5504cc452accb9e/0ca687bee460334f957552a343487418.png",
    alt: "Official dealer brands - MAHLE, Beyfren, LASO, AL-KO, FOSSER",
    title: "Trusted Brand Partners",
  },
  {
    src: "https://static.readdy.ai/image/87705512d5c7f2a4b5504cc452accb9e/4af2f3a5561ba8bd5c99066a84973d5a.png",
    alt: "MANN FILTER - Official dealer",
    title: "MANN FILTER",
  },
  {
    src: "https://static.readdy.ai/image/87705512d5c7f2a4b5504cc452accb9e/ad78cf26fcafdc9d97632dd33711509f.jpeg",
    alt: "DT Spare Parts - Official dealer",
    title: "DT Spare Parts",
  },
  {
    src: "https://static.readdy.ai/image/87705512d5c7f2a4b5504cc452accb9e/cfad8f311764d51cad8c21300c9e22bf.png",
    alt: "Febi Bilstein - Official dealer",
    title: "Febi Bilstein",
  },
  {
    src: "https://static.readdy.ai/image/87705512d5c7f2a4b5504cc452accb9e/0eff01124f7823e8efd2de3e51aef3f5.png",
    alt: "LEMA Trucks & Buses Spare Parts - Official dealer",
    title: "LEMA",
  },
  {
    src: "https://static.readdy.ai/image/87705512d5c7f2a4b5504cc452accb9e/73f72c1dee1b53eaa35eefd41ac689a1.png",
    alt: "Elring Das Original - Official dealer",
    title: "Elring",
  },
  {
    src: "https://static.readdy.ai/image/87705512d5c7f2a4b5504cc452accb9e/9788a690e63431bd4c92219e145a0452.png",
    alt: "CEI High Fidelity Spare Parts - Official dealer",
    title: "CEI",
  },
  {
    src: "https://static.readdy.ai/image/87705512d5c7f2a4b5504cc452accb9e/27f570e65cbd08bc760272f86e46ec66.jpeg",
    alt: "SABO Sabohema Automotive - Official dealer",
    title: "SABO",
  },
  {
    src: "https://static.readdy.ai/image/87705512d5c7f2a4b5504cc452accb9e/65dc365d04697c9acc5696184cc68679.png",
    alt: "Maysan Mando - Official dealer",
    title: "Maysan Mando",
  },
  {
    src: "https://static.readdy.ai/image/87705512d5c7f2a4b5504cc452accb9e/1a69af27e175285c93e56567c5d6e6c6.png",
    alt: "Dönmez - Official dealer",
    title: "Dönmez",
  },
  {
    src: "https://static.readdy.ai/image/87705512d5c7f2a4b5504cc452accb9e/3abfcc13a247ae5a069a437885f93cd6.png",
    alt: "Dikmen - Official dealer",
    title: "Dikmen",
  },
  {
    src: "https://static.readdy.ai/image/87705512d5c7f2a4b5504cc452accb9e/306aeaae6a626405cd6ac670b512ca0d.png",
    alt: "ZF - Official dealer",
    title: "ZF",
  },
  {
    src: "https://static.readdy.ai/image/87705512d5c7f2a4b5504cc452accb9e/0045f4e50a6af6a0875a98755e5b544a.png",
    alt: "Yumak Air Brake Systems - Official dealer",
    title: "Yumak",
  },
  {
    src: "https://static.readdy.ai/image/87705512d5c7f2a4b5504cc452accb9e/227449c4a00d6edfb32d68a40c8f226f.png",
    alt: "AL-KO - Official dealer",
    title: "AL-KO",
  },
  {
    src: "https://static.readdy.ai/image/87705512d5c7f2a4b5504cc452accb9e/d320c0c6eb38ae1c8f8115fd9ca9a2bf.png",
    alt: "Beyfren - Official dealer",
    title: "Beyfren",
  },
  {
    src: "https://static.readdy.ai/image/87705512d5c7f2a4b5504cc452accb9e/70a2124a33ed862ae0ebbd3067f2130c.png",
    alt: "MAHLE - Official dealer",
    title: "MAHLE",
  },
];

export default function TrustedBrands() {
  const { t } = useTranslation("common");

  /* -------------------------------------------------------------
     Helper: graceful fallback when an image cannot be loaded.
  ------------------------------------------------------------- */
  const handleImgError = (
    e: React.SyntheticEvent<HTMLImageElement, Event>
  ) => {
    const target = e.currentTarget;
    // Replace with a generic placeholder image
    target.src =
      "data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMTI4IiBoZWlnaHQ9IjEyOCIgdmlld0JveD0iMCAwIDEyOCAxMjgiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+PHJlY3Qgd2lkdGg9IjEyOCIg..."; // truncated for brevity
    target.alt = "Image not available";
  };

  return (
    <section className="py-16 bg-gray-50">
      <div className="container mx-auto px-4">
        <div className="text-center mb-12">
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
            {t("brands.title")}
          </h2>
          <p className="text-base text-gray-600 max-w-2xl mx-auto">
            {t("brands.subtitle")}
          </p>
        </div>

        <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 lg:grid-cols-6 xl:grid-cols-8 gap-4">
          {brandLogos.map((brand, idx) => (
            <div
              key={brand.title + idx}
              className="bg-white rounded-lg shadow-md p-3 flex items-center justify-center aspect-square hover:shadow-lg transition-shadow duration-300"
            >
              <img
                src={brand.src}
                alt={brand.alt}
                title={brand.title}
                className="w-full h-full object-contain"
                loading="lazy"
                onError={handleImgError}
              />
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
